/**
 * Workspace tiers — subscription-level limits for CafeLawn tenants.
 */

export type TierKey = 'free' | 'basic' | 'growth' | 'business' | 'enterprise';

export interface TierDef {
  name: string;
  description: string;
  modules: string[];
  limits: {
    maxUsers: number;
    maxOrders: number;
    maxMenuItems: number;
    maxLocations: number;
    smsQuota: number;
    dataRetentionMonths: number;
  };
  pricing: {
    monthly: number;
    yearly: number;
  };
}

export const TIERS: Record<TierKey, TierDef> = {
  free: {
    name: 'Free',
    description: 'Start without a card',
    modules: ['pos', 'orders', 'inventory', 'customers', 'payments', 'dashboard', 'settings'],
    limits: {
      maxUsers: 3,
      maxOrders: 500,
      maxMenuItems: 50,
      maxLocations: 1,
      smsQuota: 0,
      dataRetentionMonths: 6,
    },
    pricing: { monthly: 0, yearly: 0 },
  },
  basic: {
    name: 'Basic',
    description: 'For growing cafes',
    modules: ['pos', 'orders', 'inventory', 'customers', 'payments', 'dashboard', 'settings', 'analytics', 'reports'],
    limits: {
      maxUsers: 5,
      maxOrders: 2000,
      maxMenuItems: 150,
      maxLocations: 1,
      smsQuota: 100,
      dataRetentionMonths: 12,
    },
    pricing: { monthly: 999, yearly: 9990 },
  },
  growth: {
    name: 'Growth',
    description: 'Multi-staff operations',
    modules: ['pos', 'orders', 'inventory', 'customers', 'payments', 'dashboard', 'settings', 'analytics', 'reports', 'ai-chatbot', 'qr-ordering'],
    limits: {
      maxUsers: 15,
      maxOrders: 10000,
      maxMenuItems: 500,
      maxLocations: 3,
      smsQuota: 500,
      dataRetentionMonths: 24,
    },
    pricing: { monthly: 2499, yearly: 24990 },
  },
  business: {
    name: 'Business',
    description: 'Chains and franchises',
    modules: ['*'],
    limits: {
      maxUsers: 50,
      maxOrders: -1,
      maxMenuItems: -1,
      maxLocations: 10,
      smsQuota: 2000,
      dataRetentionMonths: 36,
    },
    pricing: { monthly: 4999, yearly: 49990 },
  },
  enterprise: {
    name: 'Enterprise',
    description: 'Custom scale',
    modules: ['*'],
    limits: {
      maxUsers: -1,
      maxOrders: -1,
      maxMenuItems: -1,
      maxLocations: -1,
      smsQuota: -1,
      dataRetentionMonths: -1,
    },
    pricing: { monthly: -1, yearly: -1 },
  },
};

export const TIER_ORDER: TierKey[] = ['free', 'basic', 'growth', 'business', 'enterprise'];

export const FEATURE_ROWS: { id: string; label: string }[] = [
  { id: 'pos', label: 'Point of Sale' },
  { id: 'orders', label: 'Order Management' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'customers', label: 'Customer CRM' },
  { id: 'payments', label: 'Payments' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'reports', label: 'Reports' },
  { id: 'ai-chatbot', label: 'AI Chatbot' },
  { id: 'qr-ordering', label: 'QR Ordering' },
];

export function tierHasModule(tier: TierKey, featureId: string): boolean {
  const modules = TIERS[tier].modules;
  if (modules.includes('*')) return true;
  return modules.includes(featureId);
}

export function formatLimit(value: number, suffix: string): string {
  if (value === -1) return 'Unlimited';
  return `${value} ${suffix}`;
}
